import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {VStack} from 'native-base';
import {Input, Table} from 'antd';
import filter from 'lodash/filter';
import {t} from '../../../../services/i18n';
import {fetchApi} from '../../../../services/HTTP';
import AdvanceSelect from '../../../common/AdvanceSelect';
import CustomButtonV2 from '../../../common/CustomButtonV2';
import {Colors} from '../../../../styles/Colors';
import {GlobalStyleAttributes} from '../../../../styles/GlobalStyles';
import FormInput from '../../../common/FormInput';
import AppText from '../../../common/AppText';

const TABLE_FONT_SIZE = GlobalStyleAttributes.fonts.standard16;

const ACCOUNT_CARD_SECTIONS = [
    {
        key: 'income',
        titleKey: 'account-cards-section-income',
        types: ['revenue', 'vat'],
        showAddButton: true,
    },
    {
        key: 'receipts',
        titleKey: 'account-cards-section-receipts',
        types: ['receipt'],
    },
    {
        key: 'customer',
        titleKey: 'account-cards-section-customer',
        types: ['customer'],
    },
];

const tableCellProps = () => ({style: {fontSize: TABLE_FONT_SIZE}});

const AccountCardsSettings = ({values, setFieldValue, errors, touched}) => {
    const [loading, setLoading] = useState(false);

    const fetchCards = useCallback(async (locationId) => {
        setLoading(true);
        try {
            const query = locationId ? `?locationId=${locationId}` : '';
            const res = await fetchApi(`finance/accountCards${query}`, 'GET', null, false, true);
            const cards = res.cards || [];
            setFieldValue('locationId', res.locationId);
            setFieldValue('accounting_separation', res.accounting_separation);
            setFieldValue('location_options', res.location_options || []);
            setFieldValue('cards', cards);
        } finally {
            setLoading(false);
        }
    }, [setFieldValue]);

    useEffect(() => {
        fetchCards(values.locationId);
    }, []);

    const hasUnsavedChanges = useMemo(
        () => values.cards.filter(card => card.is_new || card.is_edited).length > 0,
        [values.cards],
    );

    const onLocationChange = (option) => {
        if (hasUnsavedChanges) return;
        const locationId = option?.value ?? null;
        setFieldValue('locationId', locationId);
        fetchCards(locationId);
    };

    const getCardIndex = (cardId) => (values.cards || []).findIndex((card) => card.id === cardId);

    const getSectionCards = (types) => filter(values.cards || [], (card) => types.includes((card.type || '').toLowerCase()));

    const updateCardField = (cardId, field, value) => {
        const cards = (values.cards || []).map(card => {
            if (card.id !== cardId) return card;
            const updatedCard = {...card, [field]: value};
            if (!card.is_new) {
                updatedCard.is_edited = true;
            }
            return updatedCard;
        });
        setFieldValue('cards', cards);
    };

    const handleDeleteNewCard = (cardId) => {
        setFieldValue('cards', (values.cards || []).filter(card => card.id !== cardId));
    };

    const handleAddIncomeCardClick = () => {
        const length = values.cards?.length;
        setFieldValue('cards', [
            ...(values.cards || []),
            {
                name: '',
                account_number: '',
                type: 'revenue',
                id: length ? `new_${length + 1}` : 'new_1',
                is_new: true,
            },
        ]);
    };

    const columns = [
        {
            title: t('account-card-name'),
            dataIndex: 'name',
            key: 'name',
            onCell: tableCellProps,
            onHeaderCell: tableCellProps,
            render: (name, record) => (record.is_new || record.is_editable) ? (
                <Input
                    value={name ?? ''}
                    onChange={(e) => updateCardField(record.id, 'name', e.target.value)}
                    placeholder={t('income-card-name')}
                    status={touched?.cards?.[getCardIndex(record.id)]?.name && errors?.cards?.[getCardIndex(record.id)]?.name ? 'error' : undefined}
                />
            ) : name,
        },
        {
            title: t('account-number'),
            dataIndex: 'account_number',
            key: 'account_number',
            onCell: tableCellProps,
            onHeaderCell: tableCellProps,
            render: (accountNumber, record) => (
                <Input
                    value={accountNumber ?? ''}
                    onChange={(e) => updateCardField(record.id, 'account_number', e.target.value)}
                    placeholder={'9999'}
                />
            ),
        },
        {
            key: 'actions',
            width: 50,
            onCell: tableCellProps,
            onHeaderCell: tableCellProps,
            render: (_, record) => record.is_new ? (
                <CustomButtonV2
                    onClick={() => handleDeleteNewCard(record.id)}
                    iconName={'fa-trash-can'}
                    type={'link'}
                />
            ) : null,
        },
    ];

    return (
        <VStack space={'1.5rem'} flex={1}>
            {values.accounting_separation && (
                <FormInput label={t('account-cards-branch-select')} width={'100%'} containerProps={{mb: 0}}>
                    <AdvanceSelect
                        dontSort
                        options={values.location_options || []}
                        initValue={values.locationId}
                        onChange={onLocationChange}
                        isDisabled={hasUnsavedChanges}
                    />
                </FormInput>
            )}
            {ACCOUNT_CARD_SECTIONS.map((section) => {
                const sectionCards = getSectionCards(section.types);

                return (
                    <VStack key={section.key} space={'0.5rem'}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.heading3} mediumFont>
                            {t(section.titleKey)}
                        </AppText>
                        <Table
                            loading={loading}
                            dataSource={sectionCards.map(card => ({...card, key: card.id}))}
                            columns={columns}
                            pagination={false}
                            size="middle"
                        />
                        {section.showAddButton && (
                            <CustomButtonV2
                                type={'link'}
                                onClick={handleAddIncomeCardClick}
                                wrapperStyle={{width: 'max-content'}}
                                text={`+ ${t('add-income-card')}`}
                                color={Colors.newDesign.mainBlue}
                                textProps={{color: Colors.newDesign.mainBlue, fontSize: GlobalStyleAttributes.fonts.heading4}}
                            />
                        )}
                    </VStack>
                );
            })}
        </VStack>
    );
};

export default AccountCardsSettings;
